import { z } from 'zod';
import { matchCollettivaDiet, collettivaDietLabel } from './collettivaDiet.js';

// T-276 (collettiva): richieste di fornitura dalle destinazioni verso il centro
// cottura. È l'oggetto INVERSO del DDT in uscita (shipment): qui una destinazione
// (scuola/ospedale) chiede N pasti per un giorno di servizio, con allegati foto.
// Bozza offline-first (master sync); il responsabile la accetta e la manda in
// produzione (T-277/T-278). NON è un documento numerato: nessuna numerazione fiscale.
export const SUPPLY_REQUEST_STATUSES = [
  'draft', // bozza in compilazione
  'submitted', // inviata al centro cottura
  'accepted', // accettata dal responsabile
  'in_production', // mandata in produzione
  'fulfilled', // evasa
  'rejected', // respinta
] as const;
export const SupplyRequestStatusSchema = z.enum(SUPPLY_REQUEST_STATUSES);
export type SupplyRequestStatus = z.infer<typeof SupplyRequestStatusSchema>;

export const SUPPLY_REQUEST_STATUS_LABELS: Record<SupplyRequestStatus, string> = {
  draft: 'Bozza',
  submitted: 'Inviata',
  accepted: 'Accettata',
  in_production: 'In produzione',
  fulfilled: 'Evasa',
  rejected: 'Respinta',
};

export const SUPPLY_REQUEST_MEAL_SERVICES = ['colazione', 'pranzo', 'cena'] as const;
export const SupplyRequestMealServiceSchema = z.enum(SUPPLY_REQUEST_MEAL_SERVICES);
export type SupplyRequestMealService = z.infer<typeof SupplyRequestMealServiceSchema>;

export const SUPPLY_REQUEST_MEAL_SERVICE_LABELS: Record<SupplyRequestMealService, string> = {
  colazione: 'Colazione',
  pranzo: 'Pranzo',
  cena: 'Cena',
};

// T-441: uno slot del trittico (primo/secondo/contorno). `label` = etichetta del
// catalogo order_catalog_rows; `source` distingue auto-popolamento vs scelta manuale.
export const TritticoSlotSchema = z.object({
  label: z.string().min(1).max(200),
  source: z.enum(['auto', 'manual']).default('auto'),
});
export type TritticoSlot = z.infer<typeof TritticoSlotSchema>;

// Una riga = quanti pasti di un certo servizio/menù. recipe_id opzionale: la
// destinazione spesso chiede per servizio/menù, non per singola ricetta.
export const SupplyRequestLineSchema = z.object({
  meal_service: SupplyRequestMealServiceSchema.default('pranzo'),
  menu_label: z.string().max(40).nullable().optional(), // es. "A" / "B"
  recipe_id: z.string().uuid().nullable().optional(),
  description: z.string().min(1).max(300),
  qty: z.number().positive(), // numero di pasti/porzioni
  uom: z.string().min(1).max(20).default('pasti'),
  // T-421: reparto/sotto-gruppo della destinazione (nome, denormalizzato da
  // destinations.wards[].name). null = riga a livello destinazione. L'aggregazione
  // al piano di produzione somma comunque per label (il reparto NON esplode la
  // produzione: serve solo a ripartire l'ordine e, a valle, il carico/DDT).
  reparto: z.string().max(60).nullable().optional(),
  // T-441: TRITTICO piatti assegnato dal preposto nel Riepilogo. Ogni slot punta a
  // una LABEL di order_catalog_rows (il catalogo è già nominato per dieta; l'aggancio
  // label→ricetta resta a Pianifica). Additivi+opzionali: ogni riga esistente resta
  // valida, nessuna migration (lines è JSONB), ProductionPlanPage non li legge.
  // `source`='auto' = messo dall'auto-popolamento (ri-sovrascrivibile), 'manual' =
  // scelto dal preposto (da preservare). `trittico_locked` = riga SCORPORATA (con
  // note / override) fuori dal fan-out per-dieta.
  primo: TritticoSlotSchema.nullable().optional(),
  secondo: TritticoSlotSchema.nullable().optional(),
  contorno: TritticoSlotSchema.nullable().optional(),
  // T-445: composizione pasto a 5 slot. `frutta` = 4° slot (label catalogo FRUTTA,
  // spesso specifica: Mela per diabetico, Frutta cotta…). `bevanda` = 5° slot NUOVO,
  // default "Acqua", label "-" = nessuna bevanda. Additivi+opzionali (JSONB, no migration).
  frutta: TritticoSlotSchema.nullable().optional(),
  bevanda: TritticoSlotSchema.nullable().optional(),
  trittico_locked: z.boolean().optional(),
  // T-436: ordine per LETTO (ristorazione ospedaliera). Quando presente, la riga
  // rappresenta UN letto con la SUA dieta (qty=1). `description` porta l'etichetta
  // dieta, `notes` le specifiche (allergie, "carne frullata"…). Le righe bed-level
  // (con `letto`) e le righe catalogo (senza `letto`) sono insiemi DISGIUNTI dello
  // stesso ordine del giorno → i due flussi di inserimento coesistono senza
  // sovrascriversi. L'aggregazione al piano somma comunque per label (N letti
  // stessa dieta = N pasti), quindi il downstream è invariato.
  letto: z.string().max(40).nullable().optional(),
  notes: z.string().max(300).nullable().optional(),
});
export type SupplyRequestLine = z.infer<typeof SupplyRequestLineSchema>;

// T-436: aggregazione dell'ordine per LETTO → per ogni dieta (description),
// numero di pasti + elenco letti (con reparto e note). È la "scheda ordine unica
// sintetizzata" per la cucina: quante diete X e a quali letti. Considera solo le
// righe che portano un `letto` (bed-level); ignora le righe catalogo.
export interface CollettivaDietTotal {
  diet: string; // etichetta dieta (description della riga)
  meals: number; // numero di pasti (= numero di letti)
  beds: Array<{ letto: string; reparto: string | null; notes: string | null }>;
}
export function aggregateBedsByDiet(lines: SupplyRequestLine[]): CollettivaDietTotal[] {
  const byDiet = new Map<string, CollettivaDietTotal>();
  for (const l of lines) {
    const letto = (l.letto ?? '').trim();
    if (!letto) continue; // solo righe bed-level
    const diet = (l.description ?? '').trim();
    if (!diet) continue;
    const cur = byDiet.get(diet) ?? { diet, meals: 0, beds: [] };
    cur.meals += 1;
    cur.beds.push({ letto, reparto: l.reparto ?? null, notes: l.notes ?? null });
    byDiet.set(diet, cur);
  }
  return Array.from(byDiet.values()).sort((a, b) => b.meals - a.meals || a.diet.localeCompare(b.diet));
}

// T-441 — codice dieta ROUND-TRIP di una riga bed-level (stesso criterio di
// PrepostoBedsPage.loadDay): menu_label salvato, poi match sul testo, poi il testo.
export function bedLineDietCode(l: SupplyRequestLine): string {
  return (l.menu_label ?? '').trim() || matchCollettivaDiet(l.description) || (l.description ?? '').trim();
}

// T-441 — una riga bed-level è SCORPORATA (card e trittico propri, fuori dal
// fan-out per-dieta) se ha NOTE non vuote OPPURE è stata bloccata manualmente.
// Motivazione (vedi collettivaDiet.ts): allergie/varianti di consistenza vanno
// nelle NOTE → "ha note" = "ha un bisogno specifico" = "potrebbe non essere
// servibile col trittico base della dieta" → scorporo naturale, zero campi nuovi.
export function bedLineIsScorporata(l: SupplyRequestLine): boolean {
  return Boolean((l.notes ?? '').trim()) || l.trittico_locked === true;
}

// T-441 — split del Riepilogo per il trittico: le righe bed-level PURE (senza
// note) si aggregano per (servizio · dieta) e condividono un trittico; le righe
// con note/override si SCORPORANO in voci singole (una per letto). Chiave gruppo
// SEMPRE con meal_service: colazione/pranzo/cena non condividono il trittico.
export interface ReviewDietGroup {
  key: string; // `${meal_service}::${dietCode}`
  meal_service: SupplyRequestMealService;
  dietCode: string;
  dietLabel: string;
  meals: number;
  beds: Array<{ letto: string; reparto: string | null }>;
}
export interface ReviewScorporata {
  key: string; // identità riga: `${meal_service}|${reparto}|${letto}|${dietCode}`
  meal_service: SupplyRequestMealService;
  reparto: string | null;
  letto: string;
  dietCode: string;
  dietLabel: string;
  notes: string | null;
}
function lettoOrd(s: string): number {
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : 9999;
}
export function splitBedsForReview(lines: SupplyRequestLine[]): { dietGroups: ReviewDietGroup[]; scorporate: ReviewScorporata[] } {
  const groups = new Map<string, ReviewDietGroup>();
  const scorporate: ReviewScorporata[] = [];
  for (const l of lines) {
    const letto = (l.letto ?? '').trim();
    if (!letto) continue; // solo bed-level
    const meal = (l.meal_service ?? 'pranzo') as SupplyRequestMealService;
    const dietCode = bedLineDietCode(l);
    if (!dietCode) continue;
    const dietLabel = (l.description ?? '').trim() || collettivaDietLabel(dietCode);
    const reparto = l.reparto ?? null;
    if (bedLineIsScorporata(l)) {
      scorporate.push({ key: `${meal}|${reparto ?? ''}|${letto}|${dietCode}`, meal_service: meal, reparto, letto, dietCode, dietLabel, notes: l.notes ?? null });
    } else {
      const key = `${meal}::${dietCode}`;
      const g = groups.get(key) ?? { key, meal_service: meal, dietCode, dietLabel, meals: 0, beds: [] };
      g.meals += 1;
      g.beds.push({ letto, reparto });
      groups.set(key, g);
    }
  }
  return {
    dietGroups: Array.from(groups.values()).sort((a, b) => b.meals - a.meals || a.dietLabel.localeCompare(b.dietLabel)),
    scorporate: scorporate.sort((a, b) => (a.reparto ?? '').localeCompare(b.reparto ?? '') || lettoOrd(a.letto) - lettoOrd(b.letto)),
  };
}

export const SupplyRequestSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  destination_id: z.string().uuid(),
  requested_for_date: z.string().min(1), // YYYY-MM-DD (DB enforces date type)
  status: SupplyRequestStatusSchema.default('draft'),
  lines: z.array(SupplyRequestLineSchema).default([]),
  attachment_paths: z.array(z.string()).default([]),
  notes: z.string().nullable(),
  submitted_at: z.string().datetime().nullable(),
  submitted_by: z.string().uuid().nullable(),
  accepted_at: z.string().datetime().nullable(),
  accepted_by: z.string().uuid().nullable(),
  version: z.number().int().nonnegative().default(0),
  created_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});
export type SupplyRequest = z.infer<typeof SupplyRequestSchema>;

// Numero totale di pasti richiesti (somma delle righe).
export function supplyRequestTotalMeals(lines: SupplyRequestLine[]): number {
  return lines.reduce((acc, l) => acc + (Number.isFinite(l.qty) ? l.qty : 0), 0);
}
